import React from 'react';
import { AttemptHistoryItem } from '../types';
import { useTranslation } from '../../../i18n';

interface AttemptsStripProps {
  currentAttemptId?: string;
  attemptNumber: number;
  maxAttempts: number;
  attemptHistory?: AttemptHistoryItem[];
  onSelectAttempt?: (attemptId: string) => void;
  onRetake?: () => void;
}

export const AttemptsStrip: React.FC<AttemptsStripProps> = ({
  currentAttemptId,
  attemptNumber = 1,
  maxAttempts,
  attemptHistory = [],
  onSelectAttempt,
  onRetake,
}) => {
  const { t } = useTranslation();
  const usedCount = Math.max(attemptHistory.length, attemptNumber || 1);
  // Dynamically use maxAttempts from scenario, or fallback to usedCount (minimum 1)
  const configuredMax = typeof maxAttempts === 'number' && maxAttempts > 0 ? maxAttempts : usedCount;
  const totalSlots = Math.max(configuredMax, usedCount);

  // Calculate highest score dynamically to accurately award the BEST badge
  const validScores = attemptHistory
    .map((a) => (typeof a.overallScore === 'number' ? a.overallScore : null))
    .filter((s): s is number => s !== null && !isNaN(s));
  const maxScore = validScores.length > 0 ? Math.max(...validScores) : null;

  // Generate slots up to actual maxAttempts
  const slots = Array.from({ length: totalSlots }, (_, index) => {
    const slotNumber = index + 1;
    const historyMatch = attemptHistory.find((a) => a.attemptNumber === slotNumber);
    const score = historyMatch?.overallScore ?? (slotNumber === attemptNumber ? null : null);
    const isCurrent = historyMatch ? historyMatch.id === currentAttemptId : slotNumber === attemptNumber;
    const isUsed = slotNumber <= usedCount;
    const isBest = isUsed && typeof score === 'number' && maxScore !== null && score > 0 && score === maxScore;

    return {
      slotNumber,
      historyItem: historyMatch,
      isCurrent,
      isUsed,
      score,
      isBest,
    };
  });

  return (
    <div className="attempts-strip">
      {/* Left Label */}
      <div className="attempts-strip__meta">
        <span style={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)' }}>
          {t('attempts.strip.your_attempts', undefined, 'YOUR ATTEMPTS')}
        </span>
        <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--navy)', marginTop: '2px' }}>
          {t('attempts.strip.used_of_total', { used: usedCount, total: totalSlots }, `${usedCount} of ${totalSlots} used`)}
        </div>
        <span style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '2px', whiteSpace: 'nowrap' }}>
          {t('attempts.strip.best_counts', undefined, 'Best score counts toward result')}
        </span>
      </div>

      {/* Attempts Horizontal Row */}
      <div className="attempts-strip__cards">
        {slots.map((slot) => {
          const isSlotUsed = slot.isUsed;
          const isCurrentViewing = slot.isCurrent;
          const score = slot.score;
          const isBest = slot.isBest;
          const isPassed = slot.historyItem?.isPassed ?? (typeof score === 'number' ? score >= 70 : false);

          return (
            <div
              key={slot.slotNumber}
              onClick={() => {
                if (slot.historyItem && onSelectAttempt) {
                  onSelectAttempt(slot.historyItem.id);
                } else if (!isSlotUsed && onRetake) {
                  onRetake();
                }
              }}
              style={{
                minWidth: '148px',
                flex: '1 0 140px',
                maxWidth: '180px',
                boxSizing: 'border-box',
                overflow: 'hidden',
                padding: '9px 11px',
                borderRadius: '8px',
                border: isCurrentViewing
                  ? '1.5px solid #2563EB'
                  : '1px solid #E2E8F0',
                borderBottom: isCurrentViewing ? '3px solid #2563EB' : '1px solid #E2E8F0',
                background: isCurrentViewing
                  ? '#EFF6FF'
                  : isSlotUsed
                  ? '#FAFAFA'
                  : '#FFFFFF',
                cursor: (slot.historyItem || !isSlotUsed) ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.15s ease',
                position: 'relative',
              }}
            >
              {/* Top Row: Attempt # and Badges */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '4px',
                  flexWrap: 'wrap',
                  width: '100%',
                  boxSizing: 'border-box',
                  marginBottom: '4px',
                }}
              >
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    lineHeight: '16px',
                    color: isCurrentViewing ? '#1D4ED8' : '#64748B',
                  }}
                >
                  {t('attempts.strip.attempt_n', { n: slot.slotNumber }, `ATTEMPT ${slot.slotNumber}`)}
                </span>

                <div
                  style={{
                    display: 'flex',
                    gap: '3px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  {isBest && (
                    <span
                      style={{
                        fontSize: '8px',
                        fontWeight: 800,
                        background: '#DCFCE7',
                        color: '#166534',
                        padding: '1.5px 5px',
                        lineHeight: 1.2,
                        borderRadius: '3px',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                      }}
                    >
                      {t('attempts.strip.best', undefined, 'BEST')}
                    </span>
                  )}
                  {isCurrentViewing && (
                    <span
                      style={{
                        fontSize: '8px',
                        fontWeight: 800,
                        background: '#1D4ED8',
                        color: '#FFFFFF',
                        padding: '1.5px 5px',
                        lineHeight: 1.2,
                        borderRadius: '3px',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                      }}
                    >
                      {t('attempts.strip.viewing', undefined, 'VIEWING')}
                    </span>
                  )}
                </div>
              </div>

              {/* Main Text / Score */}
              <div>
                {isSlotUsed ? (
                  <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--navy)' }}>
                    {typeof score === 'number' ? `${Math.round(score)}%` : '—'}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748B' }}>
                    {t('attempts.strip.available', undefined, 'Available')}
                  </div>
                )}
              </div>

              {/* Dynamic Status Subtext */}
              <div style={{ marginTop: '4px', fontSize: '10.5px', fontWeight: 700 }}>
                {isSlotUsed ? (
                  typeof score === 'number' ? (
                    isPassed ? (
                      <span style={{ color: '#166534' }}>{t('attempts.strip.passed', undefined, '✓ Passed')}</span>
                    ) : (
                      <span style={{ color: '#B45309' }}>{t('attempts.strip.needs_review', undefined, '✕ Needs Review')}</span>
                    )
                  ) : (
                    <span style={{ color: '#0D4FBD' }}>{t('attempts.strip.view_details', undefined, 'View details →')}</span>
                  )
                ) : (
                  <span style={{ color: '#94A3B8' }}>{t('attempts.strip.take_attempt', undefined, 'Take attempt')}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
